import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { BarChart3, Briefcase, ClipboardList, CreditCard, FileText, House, Inbox, Languages, LogOut, MapPin, MapPinPlus, Menu, Monitor, Moon, Settings, Sun, Users, Wallet, X, BadgeCheck, UserRoundCheck } from 'lucide-react';
import type { Role } from '@wasity/shared';
import { Logo } from '@/components/ui';
import { useI18n } from '@/i18n';
import { actAs, api } from '@/lib/api';
import { useSignOut, type Me } from '@/lib/auth';
import { applyAppearance, nextAppearance, readAppearance, type Appearance } from '@/lib/theme';
import type { Queue } from '@/lib/types';

interface Item { to: string; label: string; icon: ReactNode; count?: number }

/** Live counts for the nav badges: what needs a hand right now. */
function useNavCounts(role: Role) {
  const admin = useQuery({ queryKey: ['admin', 'queue'], queryFn: () => api.get<Queue>('/api/admin/queue'), enabled: role === 'admin', refetchInterval: 60_000 });
  const broker = useQuery({ queryKey: ['broker', 'dashboard'], queryFn: () => api.get<{ incoming: unknown[]; commissionsDue: number }>('/api/broker/dashboard'), enabled: role === 'broker', refetchInterval: 60_000 });
  const evaluator = useQuery({ queryKey: ['evaluator', 'dashboard'], queryFn: () => api.get<{ incoming: unknown[] }>('/api/evaluator/dashboard'), enabled: role === 'evaluator', refetchInterval: 60_000 });
  if (role === 'admin' && admin.data) return { '/admin/queue': admin.data.brokerRequests.filter((r) => r.status === 'new').length + admin.data.evaluations.filter((e) => e.status === 'new').length, '/admin/signups': admin.data.signupsPending };
  if (role === 'broker' && broker.data) return { '/broker/incoming': broker.data.incoming.length, '/broker/commissions': broker.data.commissionsDue };
  if (role === 'evaluator' && evaluator.data) return { '/evaluator/incoming': evaluator.data.incoming.length };
  return {} as Record<string, number>;
}

function navFor(role: Role, t: (k: string) => string): Item[] {
  const i = (Icon: typeof House) => <Icon size={18} strokeWidth={2} aria-hidden />;
  if (role === 'admin')
    return [
      { to: '/admin', label: t('nav.dashboard'), icon: i(House) },
      { to: '/admin/queue', label: t('nav.queue'), icon: i(Inbox) },
      { to: '/admin/broker-requests', label: t('nav.brokerRequests'), icon: i(FileText) },
      { to: '/admin/evaluations', label: t('nav.evaluations'), icon: i(ClipboardList) },
      { to: '/admin/viewings', label: t('nav.viewings'), icon: i(MapPin) },
      { to: '/admin/users', label: t('nav.users'), icon: i(Users) },
      { to: '/admin/signups', label: t('nav.signups'), icon: i(UserRoundCheck) },
      { to: '/admin/memberships', label: t('nav.memberships'), icon: i(BadgeCheck) },
      { to: '/admin/payments', label: t('nav.payments'), icon: i(CreditCard) },
      { to: '/admin/analytics', label: t('nav.analytics'), icon: i(BarChart3) },
      { to: '/admin/settings', label: t('nav.settings'), icon: i(Settings) },
    ];
  if (role === 'broker')
    return [
      { to: '/broker', label: t('nav.dashboard'), icon: i(House) },
      { to: '/broker/incoming', label: t('nav.incoming'), icon: i(Inbox) },
      { to: '/broker/requests', label: t('nav.myRequests'), icon: i(Briefcase) },
      { to: '/broker/add-property', label: t('nav.addProperty'), icon: i(MapPinPlus) },
      { to: '/broker/listings', label: t('nav.listings'), icon: i(MapPin) },
      { to: '/broker/viewings', label: t('nav.viewings'), icon: i(ClipboardList) },
      { to: '/broker/commissions', label: t('nav.commissions'), icon: i(Wallet) },
      { to: '/broker/membership', label: t('nav.membership'), icon: i(BadgeCheck) },
    ];
  return [
    { to: '/evaluator', label: t('nav.dashboard'), icon: i(House) },
    { to: '/evaluator/incoming', label: t('nav.incoming'), icon: i(Inbox) },
    { to: '/evaluator/requests', label: t('nav.myEvaluations'), icon: i(ClipboardList) },
    { to: '/evaluator/membership', label: t('nav.membership'), icon: i(BadgeCheck) },
  ];
}

/** Sidebar on wide screens, top bar + drawer on phones — brokers accept requests from the car. */
export function Shell({ me, area }: { me: Me; area: Role }) {
  const { t, locale, setLocale } = useI18n();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const acting = me.user.role === 'admin' && area !== 'admin' ? actAs.get() : null;
  const counts = useNavCounts(area);
  const items = navFor(area, t).map((it) => ({ ...it, count: counts[it.to] }));
  const exitActing = () => {
    actAs.set(null);
    void qc.invalidateQueries();
    navigate('/admin');
  };

  const nav = (
    <nav className="flex flex-col gap-0.5" aria-label="main">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to.split('/').length === 2}
          onClick={() => setOpen(false)}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-[15px] transition-colors ${isActive ? 'bg-bg-2 text-fg font-medium' : 'text-fg-2 hover:text-fg hover:bg-bg-2/60'}`}
        >
          {it.icon}
          {it.label}
          {it.count ? <span className="ms-auto num text-[12px] font-medium bg-accent-2 text-ink rounded-sm px-1.5 min-w-5 text-center">{it.count}</span> : null}
        </NavLink>
      ))}
    </nav>
  );
  const [appearance, setAppearance] = useState<Appearance>(readAppearance);
  const footer = (
    <div className="flex flex-col gap-1 border-t border-line pt-3 mt-3">
      <p className="px-3 text-sm font-medium truncate">{acting ? acting.name : me.user.name}</p>
      <p className="px-3 text-[13px] text-muted">{acting ? `${t(`roles.${acting.role}`)} · ${t('roles.admin')}` : t(`roles.${me.user.role}`)}</p>
      <button type="button" className="flex items-center gap-3 px-3 py-2.5 text-[15px] text-fg-2 hover:text-fg" onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}>
        <Languages size={18} aria-hidden /> {t('app.language')}
      </button>
      {/* One control for three states, so it stays a single row like the language switch above it.
          The label names what you are looking at now; clicking moves to the next one. */}
      <button
        type="button"
        className="flex items-center gap-3 px-3 py-2.5 text-[15px] text-fg-2 hover:text-fg"
        aria-label={`${t('app.appearance.label')} — ${t(`app.appearance.${appearance}`)}`}
        onClick={() => { const next = nextAppearance(appearance); setAppearance(next); applyAppearance(next); }}
      >
        {appearance === 'light' ? <Sun size={18} aria-hidden /> : appearance === 'dark' ? <Moon size={18} aria-hidden /> : <Monitor size={18} aria-hidden />}
        {t('app.appearance.label')}
        <span className="ms-auto text-[13px] text-muted">{t(`app.appearance.${appearance}`)}</span>
      </button>
      <button type="button" className="flex items-center gap-3 px-3 py-2.5 text-[15px] text-fg-2 hover:text-fg" onClick={() => signOut.mutate(undefined, { onSuccess: () => navigate('/login') })}>
        <LogOut size={18} aria-hidden /> {t('app.signOut')}
      </button>
    </div>
  );

  // `minmax(0,…)` on the content column and `min-w-0` on `main`: a bare `1fr` cannot shrink below the
  // min-content of the page inside it, so one nowrap table (payments in English at 1280) pushed the whole
  // shell sideways. Capped, that table scrolls inside its own `overflow-x-auto` and the page never does.
  return (
    <div className="min-h-dvh bg-bg text-fg lg:grid lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="hidden lg:flex flex-col gap-4 border-e border-line px-4 py-6 sticky top-0 h-dvh overflow-y-auto">
        <Logo height={36} className="ms-2 mb-2 self-start" />
        {nav}
        <div className="mt-auto">{footer}</div>
      </aside>
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between h-16 px-4 border-b border-line bg-bg">
        <Logo height={30} />
        <button type="button" aria-label="menu" className="size-11 flex items-center justify-center" onClick={() => setOpen((v) => !v)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>
      {open ? (
        <div className="lg:hidden fixed inset-x-0 top-16 bottom-0 z-20 bg-bg px-4 py-4 overflow-y-auto border-t border-line">
          {nav}
          {footer}
        </div>
      ) : null}
      <main className="px-4 py-6 sm:px-8 lg:px-10 lg:py-8 max-w-[1240px] w-full min-w-0">
        {acting ? (
          <div role="status" className="flex flex-wrap items-center justify-between gap-3 mb-6 px-4 py-3 rounded-sm bg-warning-tint border border-line-2 border-s-[3px] border-s-warning text-[14px]">
            <span>{t('app.actingAs', { name: acting.name })}</span>
            <button type="button" className="font-medium text-accent hover:text-fg cursor-pointer" onClick={exitActing}>{t('app.exitActing')}</button>
          </div>
        ) : null}
        <Outlet />
      </main>
    </div>
  );
}
